/// <amd-module name="Test/TestResultDetails/Components/DebugDetails" />
import * as React from "react";
import { IViewContextData } from "Test/common-util/Common";
import * as Resources from "Test/common-util/Resources";
import * as Telemetry from "Test/common-util/Telemetry";
import { StackTrace } from "Test/TestResultDetails/Components/StackTrace";
import { DebugViewUtils } from "Test/TestResultDetails/Components/DetailsViewUtils";
import * as TelemetryConstants from "Test/TestResultDetails/TelemetryConstants";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Accordion, IAccordionItem } from "VSSUI/DeprecatedAccordion";
import { MarkdownRenderer } from "VSS/Features/Markdown/MarkdownRenderer";
import { MarkdownRendererOptions } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { filterXSS } from "xss";

export interface IDebugInfoProps extends IVssComponentProperties {
    comment?: string;
    errorMessage?: string;
    stackTrace?: string;
    linkedStackTrace?: boolean;
    viewContext?: IViewContextData;
    isFullScreen?: boolean;
}

export class DebugDetailsComponent extends VssComponent<IDebugInfoProps, {}, IVssComponentContext> {
    private _renderer: MarkdownRenderer;
    private _errorMessageRenderer: MarkdownRenderer;

    constructor(props: IDebugInfoProps, context: IVssComponentContext) {
        super(props, context);
        this._renderer = new MarkdownRenderer(context.pageContext, this._getDefaultMarkdownOptions());
        this._errorMessageRenderer = new MarkdownRenderer(context.pageContext, this._getDefaultMarkdownOptionsForErrorMessages());

        Telemetry.publishDetailsPaneEvents(this.context.pageContext, this.props.viewContext,
            props.isFullScreen ? true : false,
            TelemetryConstants.featureTestTab_DebugPivotViewed,
            {
                "commentExist": props.comment ? true : false,
                "stackTraceExist": props.stackTrace ? true : false,
            });
    }

    public render(): JSX.Element {

        const commentItems: IAccordionItem[] = [
            {
                key: "Comments",
                title: Resources.CommentsText,
                onRenderContent: () => {
                    return <div className="comment">
                        {this.props.comment && this._getMarkdownRenderedComment(this.props.comment)}
                    </div>;
                }
            }
        ];

        const errorMessageItems: IAccordionItem[] = [
            {
                key: "Comments",
                title: Resources.ErrorMessageLabel,
                onRenderContent: () => {
                    return <div className="error-message">
                        {this._markdownErrorMessageEnabledFFEnabled() ? this.props.errorMessage && this._getMarkdownRenderedErrorMessage(this.props.errorMessage) : this.props.errorMessage}
                    </div>
                }
            }
        ];

        const stackTraceItems: IAccordionItem[] = [
            {
                key: "StackTrace",
                title: Resources.StackTraceLabel,
                onRenderContent: () => {
                    return <div className="stack-trace">
                        {
                            this.props.stackTrace && this._getMarkdownRenderedStacktraceMessage(this.props.stackTrace) && this.props.viewContext &&
                            <StackTrace
                                stackTrace={this.props.stackTrace}
                                // @ts-expect-error TypeScript 4.2 upgrade
                                linkedStackTrace={this._isLinkedStackTraceFFEnabled && this.props.linkedStackTrace}
                                viewContext={this.props.viewContext}>
                            </StackTrace>
                        }
                    </div>
                }
            }
        ];

        return (
            <div className="DebugDetails-view">
                {
                    this.props.comment != null && this.props.comment !== "" &&
                    <Accordion
                        initiallyExpandAll={true}
                        items={commentItems}
                    >
                    </Accordion>
                }
                {
                    this.props.errorMessage != null &&
                    <Accordion
                        className="error-message-accordion"
                        initiallyExpandAll={true}
                        items={errorMessageItems}>
                    </Accordion>
                }
                {
                    this.props.stackTrace != null &&
                    <Accordion
                        className="stack-trace-accordion"
                        initiallyExpandAll={true}
                        items={stackTraceItems}>
                    </Accordion>
                }
            </div>
        );
    }

    private _getMarkdownRenderedComment(comment: string): JSX.Element {
        const res = this._renderer.renderHtml(comment);
        if (DebugViewUtils.stripHtml(res.trim()) !== comment.trim())
        {
            Telemetry.publishDetailsPaneEvents(this.context.pageContext, this.props.viewContext,
                this.props.isFullScreen ? true : false,
                TelemetryConstants.featureTestTab_markdownViewed,
                {
                    "Comment": true
                });
        }
        const sanitizedInput = filterXSS(res);
        const resolvedMarkdown = {
            __html: sanitizedInput
        };
        /* tslint:disable:react-no-dangerous-html */
        return <div className={"test-comment-markdown-renderer"}
            dangerouslySetInnerHTML={resolvedMarkdown} />
        /* tslint:enable:react-no-dangerous-html */
    }

    private _getMarkdownRenderedErrorMessage(ErrorMessage: string): JSX.Element {
        const res = this._errorMessageRenderer.renderHtml(ErrorMessage);
        if (DebugViewUtils.stripHtml(res.trim()) !== ErrorMessage.trim())
        {
            Telemetry.publishDetailsPaneEvents(this.context.pageContext, this.props.viewContext,
                this.props.isFullScreen ? true : false,
                TelemetryConstants.featureTestTab_markdownViewed,
                {
                    "ErrorMessage": true
                });
        }
        const sanitizedInput = filterXSS(res);
        const resolvedMarkdown = {
            __html: sanitizedInput
        };
        /* tslint:disable:react-no-dangerous-html */
        return <div className={"test-error-markdown-renderer"}
            dangerouslySetInnerHTML={resolvedMarkdown} />
        /* tslint:enable:react-no-dangerous-html */
    }

    private _getMarkdownRenderedStacktraceMessage(StackTraceMessage: string): JSX.Element {
        const res = this._renderer.renderHtml(StackTraceMessage);
        const sanitizedInput = filterXSS(res);
        const resolvedMarkdown = {
            __html: sanitizedInput
        };
        return <div className={"test-stackTrace-markdown-renderer"}
            dangerouslySetInnerHTML={resolvedMarkdown} />
    }

    private _getDefaultMarkdownOptions(): any {
        return {
            validateLink: validateLinkProtocol,
            enableEmoji:true,
            breaks:true,
            html: true,
            enableHeaderAnchorSharing: this.props.enableHeaderAnchorSharing,
            enableYaml: true,
        };
    }

    private _getDefaultMarkdownOptionsForErrorMessages(): any {
        return {
            validateLink: validateLinkProtocol,
            enableEmoji:false,
            breaks:false,
            html: true,
            enableHeaderAnchorSharing: this.props.enableHeaderAnchorSharing,
            enableYaml: true,
        };
    }

    private _isLinkedStackTraceFFEnabled(): boolean {
        return isFeatureFlagEnabled(this.context.pageContext, DebugDetailsComponent._linkedStackTraceEnabledFFName, false)
    }

    private _markdownErrorMessageEnabledFFEnabled(): boolean {
        return isFeatureFlagEnabled(this.context.pageContext, DebugDetailsComponent._markdownErrorMessageEnabledFFName, false)
    }

    private static readonly _linkedStackTraceEnabledFFName = "WebAccess.TestManagement.LinkedStackTrace";
    private static readonly _markdownErrorMessageEnabledFFName = "WebAccess.TestManagement.MarkdownErrorMessage";
}

// Inspect the provided urls for all protocols we want to prevent
// The markdown-it defaults are to allow the data protocol if it is an image but
// we don't want to allow embeded images that we'll have to scan for malicious content
const BAD_PROTOCOLS = /^(vbscript|javascript|file|data):/;
export function validateLinkProtocol(url: any) {
    const str = url.trim().toLowerCase();
    return !BAD_PROTOCOLS.test(str);
}
